import React, { useEffect, useState } from "react";
import { Plus, Trash2, CalendarRange, Zap, Loader2, Clock, Sparkles, Bot, Image as ImageIcon, X, Wand2, Pencil, Eye } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Calendar } from "@/components/ui/calendar";
import { PlatformIcon, PlatformToggle, ALL_PLATFORMS, getPlatformMeta } from "@/components/PlatformIcon";

const PLATFORM_COLORS = {
  facebook: "bg-blue-900/30 border-blue-500/50 text-blue-300",
  instagram: "bg-pink-900/30 border-pink-500/50 text-pink-300",
  x: "bg-zinc-900/40 border-zinc-500/50 text-zinc-300",
  tiktok: "bg-rose-900/30 border-rose-500/50 text-rose-300",
  youtube: "bg-red-900/30 border-red-500/50 text-red-300",
  linkedin: "bg-sky-900/30 border-sky-500/50 text-sky-300",
  whatsapp: "bg-emerald-900/30 border-emerald-500/50 text-emerald-300",
  threads: "bg-zinc-900/40 border-zinc-400/50 text-zinc-200",
  pinterest: "bg-red-900/30 border-red-500/50 text-red-300",
  telegram: "bg-sky-900/30 border-sky-400/50 text-sky-300",
  discord: "bg-indigo-900/30 border-indigo-500/50 text-indigo-300",
  bluesky: "bg-blue-900/30 border-blue-400/50 text-blue-300",
  email: "bg-amber-900/30 border-amber-500/50 text-amber-300",
};

export default function CalendarPage() {
  const [posts, setPosts] = useState([]);
  const [selected, setSelected] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ platform: "instagram", content: "", time: "12:00", image_url: "", image_alt: "" });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [editingId, setEditingId] = useState(null);  // post_id when editing an existing post
  const imageInputRef = React.useRef(null);

  const [variants, setVariants] = useState([]);   // last AI-generated batch — user picks one to attach
  const generateImage = async () => {
    if (!form.content.trim()) { toast.error("Type the post content first — the AI uses it to set the scene."); return; }
    setGeneratingImage(true);
    setVariants([]);
    try {
      const { data } = await api.post("/schedule/generate-image", {
        platform: form.platform, content: form.content, variants: 3,
      });
      const v = data.variants || (data.image_url ? [{ image_url: data.image_url }] : []);
      setVariants(v);
      if (v.length === 1) {
        // Single variant → attach immediately (legacy 1-shot behaviour)
        setForm((f) => ({ ...f, image_url: v[0].image_url }));
        toast.success("AI image attached");
      } else if (v.length > 1) {
        toast.success(`${v.length} variants generated — pick the strongest`);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 402) toast.error("Image-generation budget exhausted — top up Universal LLM Key.", { duration: 8000 });
      else toast.error(err?.response?.data?.detail || "Image generation failed");
    } finally { setGeneratingImage(false); }
  };

  const pickVariant = (url) => {
    setForm((f) => ({ ...f, image_url: url }));
    setVariants([]);
    toast.success("Image picked");
  };

  const openEdit = (post) => {
    setEditingId(post.post_id);
    const dt = new Date(post.scheduled_for);
    setSelected(new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()));
    const hh = String(dt.getHours()).padStart(2, "0");
    const mm = String(dt.getMinutes()).padStart(2, "0");
    setForm({
      platform: post.platform || "instagram",
      content: post.content || "",
      time: `${hh}:${mm}`,
      image_url: post.image_url || "",
      image_alt: post.image_alt || "",
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image must be ≤ 8 MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Please pick an image file"); return; }
    setUploadingImage(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/schedule/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.image_url }));
      toast.success("Image attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Image upload failed");
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  };

  const removeImage = () => { setForm((f) => ({ ...f, image_url: "", image_alt: "" })); setVariants([]); };

  const load = async () => {
    const res = await api.get("/schedule");
    setPosts(res.data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.content.trim()) { toast.error("Content required"); return; }
    const [h, m] = form.time.split(":");
    const dt = new Date(selected);
    dt.setHours(Number(h) || 12, Number(m) || 0, 0, 0);
    try {
      if (editingId) {
        await api.patch(`/schedule/${editingId}`, {
          platform: form.platform,
          content: form.content,
          scheduled_for: dt.toISOString(),
          image_url: form.image_url || "",
          image_alt: form.image_alt || "",
        });
        toast.success("Post updated");
      } else {
        await api.post("/schedule", {
          platform: form.platform,
          content: form.content,
          scheduled_for: dt.toISOString(),
          image_url: form.image_url || "",
          image_alt: form.image_alt || "",
        });
        toast.success("Scheduled");
      }
      setShowModal(false);
      setEditingId(null);
      setForm({ platform: "instagram", content: "", time: "12:00", image_url: "", image_alt: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const del = async (id) => {
    await api.delete(`/schedule/${id}`);
    setPosts(posts.filter((p) => p.post_id !== id));
    toast.success("Removed");
  };

  const sameDay = (a, b) => {
    const x = new Date(a), y = new Date(b);
    return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
  };
  const dayPosts = posts.filter((p) => sameDay(p.scheduled_for, selected));
  const upcoming = posts.filter((p) => new Date(p.scheduled_for) >= new Date()).slice(0, 10);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="calendar-page">
      <PageHeader
        eyebrow="// SCHEDULE"
        title="Plan your broadcast calendar."
        description="Drop posts onto dates. Track them across every platform."
        action={
          <button
            onClick={() => setShowModal(true)}
            data-testid="schedule-create-btn"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-5 py-2.5 font-semibold text-black amber-glow"
          >
            <Plus className="h-4 w-4" /> Schedule post
          </button>
        }
      />

      <AutoPosterCard reloadPosts={load} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="rounded-xl border border-line bg-bg-surface p-4 lg:col-span-2" data-testid="calendar-widget">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={(d) => d && setSelected(d)}
            className="bg-bg-surface text-ink-primary [&_button]:text-ink-primary [&_button:hover]:bg-bg-elevated [&_.rdp-day_selected]:bg-brand-amber [&_.rdp-day_selected]:text-black"
          />
          <div className="mt-4 border-t border-line pt-4">
            <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber mb-3">
              {selected.toDateString()} — {dayPosts.length} post{dayPosts.length !== 1 ? "s" : ""}
            </div>
            {dayPosts.length === 0 ? (
              <div className="text-sm text-ink-muted">Nothing scheduled for this day.</div>
            ) : (
              <div className="space-y-2">
                {dayPosts.map((p) => (
                  <div key={p.post_id} className={`rounded-lg border p-3 ${PLATFORM_COLORS[p.platform] || ""}`} data-testid={`day-post-${p.post_id}`}>
                    <div className="flex items-center justify-between mb-1 gap-2">
                      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider">
                        <PlatformIcon platform={p.platform} className="h-3 w-3 shrink-0" />
                        {p.platform}
                      </span>
                      <div className="flex items-center gap-1.5">
                        {p.image_url && <ImageIcon className="h-3 w-3 opacity-70" title="Has image" />}
                        <button
                          onClick={() => openEdit(p)}
                          title="Edit post"
                          data-testid={`day-post-edit-${p.post_id}`}
                          className="opacity-60 hover:opacity-100 hover:text-cyan-300 transition"
                        ><Pencil className="h-3 w-3" /></button>
                        <button onClick={() => del(p.post_id)} title="Delete" className="opacity-50 hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </div>
                    <p className="text-xs line-clamp-2">{p.content}</p>
                    <div className="text-[10px] mt-1 opacity-70">{new Date(p.scheduled_for).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-line bg-bg-surface p-6" data-testid="upcoming-panel">
          <h3 className="font-display text-lg font-bold mb-4">Upcoming</h3>
          {upcoming.length === 0 ? (
            <div className="text-center py-12 text-ink-muted">
              <CalendarRange className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <div className="text-sm">Calendar is clear.</div>
            </div>
          ) : (
            <div className="space-y-3">
              {upcoming.map((p) => (
                <div key={p.post_id} className={`border-l-2 pl-3 py-1 ${p.media_kind === "video" ? "border-violet-500/60" : "border-brand-amber/50"}`} data-testid={`upcoming-${p.post_id}`}>
                  <div className={`font-mono text-[10px] uppercase tracking-wider ${p.media_kind === "video" ? "text-violet-300" : "text-brand-amber"}`}>
                    {new Date(p.scheduled_for).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    {p.media_kind === "video" && <span className="ml-2 px-1.5 py-0.5 rounded bg-violet-500/15 border border-violet-500/40 text-[9px]">VIDEO</span>}
                  </div>
                  <div className="text-xs text-ink-secondary mt-1 flex items-center gap-1.5">
                    <PlatformIcon platform={p.platform} className="h-3 w-3 shrink-0" style={{ color: getPlatformMeta(p.platform)?.color }} />
                    <span className="capitalize shrink-0">{p.platform}</span> · <span className="text-ink-primary line-clamp-1">{p.content}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-bg-surface border-line text-ink-primary !max-w-4xl" data-testid="schedule-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editingId ? "Edit scheduled post" : "Schedule a post"}</DialogTitle>
            <DialogDescription className="text-ink-secondary">
              On {selected.toDateString()}
            </DialogDescription>
          </DialogHeader>
          <div className="grid md:grid-cols-2 gap-5 py-2">
            {/* LEFT — Form */}
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-secondary">Platform</Label>
                <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                  <SelectTrigger className="mt-1 bg-bg border-line" data-testid="schedule-platform"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-bg-elevated border-line">
                    {ALL_PLATFORMS.map((p) => (
                      <SelectItem key={p} value={p}>
                        <span className="inline-flex items-center gap-2">
                          <PlatformIcon platform={p} className="h-3.5 w-3.5" style={{ color: getPlatformMeta(p)?.color }} />
                          {getPlatformMeta(p)?.label || p}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-secondary">Time (HH:MM)</Label>
                <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="mt-1 bg-bg border-line" data-testid="schedule-time" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-secondary">Content</Label>
                <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={4} className="mt-1 bg-bg border-line" data-testid="schedule-content" />
                <div className="mt-1 text-[10px] text-ink-muted text-right">{(form.content || "").length} chars</div>
              </div>

              {/* Image attachment */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-ink-secondary">Image (optional)</Label>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  className="hidden"
                  data-testid="schedule-image-input"
                />
                {!form.image_url ? (
                  <>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        disabled={uploadingImage || generatingImage}
                        data-testid="schedule-image-upload-btn"
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-dashed border-line bg-bg-elevated px-3 py-3 text-xs text-ink-secondary hover:text-cyan-300 hover:border-cyan-500/60 transition disabled:opacity-50"
                      >
                        {uploadingImage
                          ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</>
                          : <><ImageIcon className="h-4 w-4" /> Upload</>}
                      </button>
                      <button
                        type="button"
                        onClick={generateImage}
                        disabled={uploadingImage || generatingImage}
                        data-testid="schedule-image-generate-btn"
                        title="AI generates 3 on-brand variants — pick the strongest"
                        className="inline-flex items-center justify-center gap-2 rounded-md border border-brand-amber/50 bg-brand-amber/10 px-3 py-3 text-xs text-brand-amber hover:bg-brand-amber/20 transition disabled:opacity-50"
                      >
                        {generatingImage
                          ? <><Loader2 className="h-4 w-4 animate-spin" /> Generating 3 variants…</>
                          : <><Wand2 className="h-4 w-4" /> Generate 3 with AI</>}
                      </button>
                    </div>

                    {/* AI Variant gallery — click one to attach */}
                    {variants.length > 1 && (
                      <div className="mt-2" data-testid="schedule-variants">
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-brand-amber mb-1.5">
                          // pick a variant
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          {variants.map((v, i) => (
                            <button
                              type="button"
                              key={i}
                              onClick={() => pickVariant(v.image_url)}
                              data-testid={`schedule-variant-${i}`}
                              title={v.angle || `Variant ${i + 1}`}
                              className="group relative rounded-md overflow-hidden border-2 border-line bg-bg hover:border-brand-amber transition focus:outline-none"
                            >
                              <img src={v.image_url} alt={`Variant ${i + 1}`} className="w-full aspect-square object-cover group-hover:opacity-90 transition" />
                              <span className="absolute top-1 left-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-bg/70 text-[10px] font-bold text-brand-amber backdrop-blur">
                                {i + 1}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="mt-1 relative rounded-md border border-line bg-bg-elevated overflow-hidden" data-testid="schedule-image-preview">
                    <img src={form.image_url} alt={form.image_alt || "Attachment preview"} className="w-full max-h-48 object-cover" />
                    <button type="button" onClick={removeImage} title="Remove image" data-testid="schedule-image-remove"
                            className="absolute top-2 right-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-bg/80 text-rose-300 hover:text-rose-200 hover:bg-bg backdrop-blur transition">
                      <X className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={generateImage} disabled={generatingImage} title="Regenerate with AI"
                            data-testid="schedule-image-regen"
                            className="absolute top-2 left-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-bg/80 text-brand-amber hover:bg-bg backdrop-blur transition">
                      {generatingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                    </button>
                    <Input value={form.image_alt} onChange={(e) => setForm({ ...form, image_alt: e.target.value })}
                           placeholder="Alt text (accessibility + SEO)"
                           className="border-0 border-t border-line bg-bg-surface text-xs rounded-none"
                           data-testid="schedule-image-alt" />
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT — Live Preview */}
            <div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-300 mb-2 flex items-center gap-1.5">
                <Eye className="h-3 w-3" /> Live preview · {form.platform}
              </div>
              <PostPreview platform={form.platform} content={form.content} imageUrl={form.image_url} scheduledFor={(() => {
                const dt = new Date(selected);
                const [h, m] = (form.time || "12:00").split(":");
                dt.setHours(Number(h) || 12, Number(m) || 0, 0, 0);
                return dt;
              })()} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => { setShowModal(false); setEditingId(null); }} className="rounded-md border border-line bg-bg-elevated px-4 py-2 text-sm text-ink-secondary hover:text-ink-primary transition" data-testid="schedule-cancel">
              Cancel
            </button>
            <button onClick={create} data-testid="schedule-submit" className="rounded-md bg-brand-amber px-5 py-2 font-semibold text-black amber-glow">
              {editingId ? "Save changes" : "Schedule"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Auto-Poster Card ============
const ALL_DAYS = [
  { id: "mon", label: "Mo" }, { id: "tue", label: "Tu" }, { id: "wed", label: "We" },
  { id: "thu", label: "Th" }, { id: "fri", label: "Fr" }, { id: "sat", label: "Sa" },
  { id: "sun", label: "Su" },
];
const TZ_OPTIONS = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "Europe/London", "Europe/Berlin", "Europe/Paris", "Africa/Lagos", "Asia/Kolkata",
  "Asia/Tokyo", "Asia/Singapore", "Australia/Sydney",
];

function AutoPosterCard({ reloadPosts }) {
  const [cfg, setCfg] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const [c, h] = await Promise.all([
        api.get("/auto-poster/config"),
        api.get("/auto-poster/history").catch(() => ({ data: [] })),
      ]);
      setCfg(c.data);
      setHistory(h.data || []);
    } catch { /* */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setBusy(true);
    try {
      const { data } = await api.patch("/auto-poster/config", next);
      setCfg(data);
      toast.success(next.enabled ? "Auto-poster ON" : "Saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };
  const update = (patch) => save({ ...cfg, ...patch });
  const toggleDay = (d) => {
    const has = (cfg.days || []).includes(d);
    update({ days: has ? cfg.days.filter((x) => x !== d) : [...(cfg.days || []), d] });
  };
  const togglePlat = (p) => {
    const has = (cfg.platforms || []).includes(p);
    update({ platforms: has ? cfg.platforms.filter((x) => x !== p) : [...(cfg.platforms || []), p] });
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/auto-poster/run-now");
      toast.success(`Posted ${data.run.post_count} drafts for "${data.run.topic.slice(0, 40)}…"`);
      await load();
      if (reloadPosts) await reloadPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally { setRunning(false); }
  };

  if (!cfg) return null;

  return (
    <section
      className={`mb-6 rounded-xl border p-5 transition ${
        cfg.enabled
          ? "border-brand-amber/40 bg-gradient-to-br from-brand-amber/5 to-transparent"
          : "border-line bg-bg-surface"
      }`}
      data-testid="auto-poster-card"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-md flex items-center justify-center ${
            cfg.enabled ? "bg-brand-amber/20 text-brand-amber" : "bg-bg-elevated text-ink-muted"
          }`}>
            <Bot className="h-5 w-5" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber/80">// AUTO-POSTER</div>
            <div className="font-display text-xl font-bold">Hands-free social on a schedule</div>
            <p className="text-xs text-ink-muted">AI ideates today&apos;s topic + writes copy for every platform, on the days &amp; time you pick. Each run lands here as draft posts.</p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <Switch checked={!!cfg.enabled} onCheckedChange={(v) => update({ enabled: v })} disabled={busy} data-testid="auto-poster-toggle" />
          <button
            onClick={runNow}
            disabled={running || busy}
            data-testid="auto-poster-run-now"
            className="inline-flex items-center gap-1.5 rounded-md border border-brand-amber/40 bg-brand-amber/10 hover:bg-brand-amber/20 px-3 py-1.5 text-xs font-semibold text-brand-amber disabled:opacity-50 transition"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            Run now
          </button>
        </div>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-4">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-secondary">Time</Label>
          <div className="mt-1 flex items-center gap-2">
            <Input
              type="number" min="0" max="23"
              value={cfg.hour ?? 9}
              onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) || 0 })}
              onBlur={() => update({ hour: cfg.hour })}
              data-testid="auto-poster-hour"
              className="bg-bg border-line w-16 text-center font-mono"
            />
            <span className="text-ink-muted">:</span>
            <Input
              type="number" min="0" max="59"
              value={cfg.minute ?? 0}
              onChange={(e) => setCfg({ ...cfg, minute: Number(e.target.value) || 0 })}
              onBlur={() => update({ minute: cfg.minute })}
              data-testid="auto-poster-minute"
              className="bg-bg border-line w-16 text-center font-mono"
            />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-secondary">Timezone</Label>
          <Select value={cfg.timezone || "UTC"} onValueChange={(v) => update({ timezone: v })}>
            <SelectTrigger className="mt-1 bg-bg border-line" data-testid="auto-poster-tz"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line max-h-72">
              {TZ_OPTIONS.map((tz) => <SelectItem key={tz} value={tz} className="text-xs">{tz}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-secondary">Tone</Label>
          <Select value={cfg.tone || "energetic"} onValueChange={(v) => update({ tone: v })}>
            <SelectTrigger className="mt-1 bg-bg border-line" data-testid="auto-poster-tone"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-bg-elevated border-line">
              {["energetic", "professional", "playful", "moody", "hype"].map((t) => (
                <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-secondary">Topic hint (optional)</Label>
          <Input
            value={cfg.topic_hint || ""}
            onChange={(e) => setCfg({ ...cfg, topic_hint: e.target.value })}
            onBlur={() => update({ topic_hint: cfg.topic_hint || "" })}
            placeholder="e.g. Highlight new releases"
            className="mt-1 bg-bg border-line"
            data-testid="auto-poster-hint"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1.5 block">Days</Label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_DAYS.map((d) => {
              const on = (cfg.days || []).includes(d.id);
              return (
                <button key={d.id} onClick={() => toggleDay(d.id)} data-testid={`auto-poster-day-${d.id}`}
                  className={`h-8 w-9 rounded-md border text-xs font-mono transition ${
                    on ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                       : "border-line bg-bg-elevated text-ink-muted hover:text-ink-primary"
                  }`}>{d.label}</button>
              );
            })}
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider text-ink-secondary mb-1.5 block">Platforms</Label>
          <div className="flex flex-wrap gap-1.5">
            {ALL_PLATFORMS.map((p) => (
              <PlatformToggle
                key={p}
                platform={p}
                on={(cfg.platforms || []).includes(p)}
                onClick={() => togglePlat(p)}
                testid={`auto-poster-plat-${p}`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Newsroom hourly pipeline */}
      <div className="mt-5 border-t border-line/60 pt-4" data-testid="auto-poster-newsroom">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-brand-amber" />
            <span className="font-display font-bold text-sm">Hourly newsroom</span>
            <span className="text-[10px] font-mono text-ink-muted">// pulls RSS + auto-generates + auto-publishes approved stories</span>
          </div>
          <Switch checked={!!cfg.news_enabled} onCheckedChange={(v) => update({ news_enabled: v })} disabled={busy} data-testid="auto-poster-news-toggle" />
        </div>
        {cfg.news_enabled && (
          <div className="mt-2 text-[11px] text-ink-muted space-y-2">
            <div>Runs at the top of every hour in <span className="text-ink-primary">{cfg.timezone || "UTC"}</span>. Ingests Billboard / Rolling Stone / Variety / MBW / Grammy / Complex / Pitchfork / AP / BBC / ESPN → AI-rewrites 3 fresh drafts → AI trust-scores each → auto-approves anything ≥ threshold → pushes approved+auto-publish stories onto the calendar.</div>
            <div className="flex items-center gap-2">
              <span>Auto-approve threshold:</span>
              <Input
                type="number" min="0" max="100" step="5"
                value={cfg.trust_auto_approve_threshold ?? 80}
                onChange={(e) => setCfg({ ...cfg, trust_auto_approve_threshold: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })}
                onBlur={() => update({ trust_auto_approve_threshold: cfg.trust_auto_approve_threshold ?? 80 })}
                className="w-20 h-7 bg-bg border-line font-mono text-center"
                data-testid="auto-poster-trust-threshold"
              />
              <span className="text-ink-muted">/ 100 — higher = more conservative</span>
            </div>

            {/* Auto-render social video */}
            <div className="mt-3 flex items-center justify-between gap-3 rounded-md border border-line bg-bg-elevated/60 px-3 py-2">
              <div>
                <div className="flex items-center gap-1.5 text-ink-primary">
                  <span className="text-[11px] font-semibold">Auto-render social video</span>
                </div>
                <div className="text-[10px] text-ink-muted">For every auto-published story, also render a branded vertical MP4 (Reels · Shorts · TikTok-ready).</div>
              </div>
              <Switch
                checked={!!cfg.news_video_auto}
                onCheckedChange={(v) => update({ news_video_auto: v })}
                disabled={busy}
                data-testid="auto-poster-news-video-toggle"
              />
            </div>

            {cfg.news_video_auto && (
              <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
                <label className="rounded-md border border-line bg-bg-elevated/60 p-2">
                  <span className="block text-[10px] uppercase tracking-wider text-ink-muted mb-1">Format</span>
                  <select
                    value={cfg.news_video_fmt || "vertical"}
                    onChange={(e) => update({ news_video_fmt: e.target.value })}
                    disabled={busy}
                    className="w-full bg-bg border border-line rounded px-2 py-1 text-ink-primary text-xs"
                    data-testid="auto-poster-news-video-fmt"
                  >
                    <option value="vertical">9:16 Vertical (Reels · Shorts · TikTok)</option>
                    <option value="square">1:1 Square (IG · FB feed)</option>
                    <option value="landscape">16:9 Landscape (YouTube · X)</option>
                  </select>
                </label>
                <label className="rounded-md border border-line bg-bg-elevated/60 p-2">
                  <span className="block text-[10px] uppercase tracking-wider text-ink-muted mb-1">Theme</span>
                  <select
                    value={cfg.news_video_theme || "amber_dark"}
                    onChange={(e) => update({ news_video_theme: e.target.value })}
                    disabled={busy}
                    className="w-full bg-bg border border-line rounded px-2 py-1 text-ink-primary text-xs"
                    data-testid="auto-poster-news-video-theme"
                  >
                    <option value="amber_dark">Studio Amber</option>
                    <option value="violet_dark">Violet Nebula</option>
                    <option value="ember_red">Ember Red</option>
                    <option value="ocean_blue">Ocean Blue</option>
                    <option value="broadcast_green">Broadcast Green</option>
                  </select>
                </label>
              </div>
            )}

            {cfg.news_video_auto && (
              <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-line bg-bg-elevated/60 px-3 py-2">
                <div>
                  <div className="flex items-center gap-1.5 text-ink-primary">
                    <span className="text-[11px] font-semibold">Auto-schedule the MP4 onto social</span>
                  </div>
                  <div className="text-[10px] text-ink-muted">
                    Drops the rendered video into the calendar for IG / TikTok / YT Shorts (vertical), IG / FB (square), or YT / X / FB (landscape) — uses the same caption as the text post.
                  </div>
                </div>
                <Switch
                  checked={cfg.news_video_auto_post !== false}
                  onCheckedChange={(v) => update({ news_video_auto_post: v })}
                  disabled={busy}
                  data-testid="auto-poster-news-video-autopost"
                />
              </div>
            )}

            {/* Auto-publish to WordPress */}
            <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-line bg-bg-elevated/60 px-3 py-2">
              <div>
                <div className="flex items-center gap-1.5 text-ink-primary">
                  <span className="text-[11px] font-semibold">Auto-publish to WordPress</span>
                </div>
                <div className="text-[10px] text-ink-muted">
                  Every auto-published story also drops a draft into your WordPress blog with the AI headline, article, tags, SEO &amp; featured image. Requires Blog Connector to be set up.
                </div>
              </div>
              <Switch
                checked={!!cfg.news_wp_auto_publish}
                onCheckedChange={(v) => update({ news_wp_auto_publish: v })}
                disabled={busy}
                data-testid="auto-poster-news-wp-toggle"
              />
            </div>

            {cfg.news_wp_auto_publish && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <label className="rounded-md border border-line bg-bg-elevated/60 p-2">
                  <span className="block text-[10px] uppercase tracking-wider text-ink-muted mb-1">WP publish mode</span>
                  <select
                    value={cfg.news_wp_status || "draft"}
                    onChange={(e) => update({ news_wp_status: e.target.value })}
                    disabled={busy}
                    className="w-full bg-bg border border-line rounded px-2 py-1 text-ink-primary text-xs"
                    data-testid="auto-poster-news-wp-status"
                  >
                    <option value="draft">Draft (editor reviews)</option>
                    <option value="publish">Publish immediately (no review)</option>
                  </select>
                </label>
                <a
                  href="/dashboard/blog-connector"
                  className="rounded-md border border-sky-500/40 bg-sky-500/10 p-2 text-[11px] text-sky-300 hover:bg-sky-500/20 flex items-center justify-center gap-1"
                  data-testid="auto-poster-news-wp-connect-link"
                >
                  Open Blog Connector →
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {history.length > 0 && (
        <div className="mt-5 border-t border-line/60 pt-4" data-testid="auto-poster-history">
          <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-2">Last runs</div>
          <div className="space-y-1.5">
            {history.slice(0, 4).map((r) => (
              <div key={r.run_id} className="flex items-center justify-between gap-3 rounded-md border border-line bg-bg-elevated/60 px-3 py-2 text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <Sparkles className="h-3 w-3 text-brand-amber shrink-0" />
                  <span className="truncate text-ink-primary">{r.topic}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0 text-ink-muted">
                  <span className="font-mono">{r.post_count} posts</span>
                  <span className="font-mono text-[10px] flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(r.ran_at).toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}


/**
 * PostPreview — platform-styled mockup of what the post will look like once
 * published. Loads the station's name + logo once, then renders a card that
 * mirrors each platform's visual conventions (square crop for IG/FB, landscape
 * for X/LinkedIn, vertical for TikTok). Useful as a sanity-check before
 * scheduling and before hitting Save when editing an existing post.
 */
function PostPreview({ platform, content, imageUrl, scheduledFor }) {
  const [station, setStation] = React.useState(null);
  React.useEffect(() => {
    api.get("/stations/me").then((r) => setStation(r.data)).catch(() => {});
  }, []);
  const aspect = (() => {
    switch ((platform || "").toLowerCase()) {
      case "x": case "twitter": case "linkedin": case "youtube": return "aspect-video";    // 16:9
      case "tiktok":                                            return "aspect-[9/16]";   // 9:16
      default:                                                   return "aspect-square";   // 1:1
    }
  })();
  const handle = (station?.name || "Your Station").toLowerCase().replace(/\s+/g, "");
  const time = scheduledFor ? new Date(scheduledFor).toLocaleString(undefined, {
    weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  }) : "—";

  return (
    <div className="rounded-xl border border-line bg-bg-elevated overflow-hidden" data-testid="schedule-post-preview">
      {/* Header strip — mimics each platform's account row */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-line">
        {station?.logo_url ? (
          <img src={station.logo_url} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-bg-surface" />
        )}
        <div className="min-w-0 flex-1">
          <div className="text-xs font-semibold text-ink-primary truncate">{station?.name || "Your Station"}</div>
          <div className="text-[10px] text-ink-muted truncate">
            @{handle} · <span className="text-cyan-300">{time}</span>
          </div>
        </div>
        <span className="font-mono text-[9px] uppercase tracking-wider text-ink-muted shrink-0">{platform}</span>
      </div>

      {/* Image area */}
      {imageUrl ? (
        <div className={`${aspect} bg-bg overflow-hidden`}>
          <img src={imageUrl} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`${aspect} bg-gradient-to-br from-bg-surface to-bg flex items-center justify-center text-ink-muted text-[10px] uppercase tracking-wider font-mono`}>
          // no image · upload or generate
        </div>
      )}

      {/* Caption */}
      <div className="px-3 py-3">
        <p className="text-xs text-ink-primary whitespace-pre-wrap break-words line-clamp-6">
          {content || <span className="text-ink-muted italic">// type your post copy on the left…</span>}
        </p>
      </div>
    </div>
  );
}

